import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import api, { resolveMediaUrl } from "../lib/api";

export default function MediaDetail() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/media/${id}`).then((r) => setM(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) {
    return (
      <Layout>
        <div className="px-4 md:px-6 py-20">
          <div className="hairline p-12 text-center">
            <div className="heavy text-4xl">Transmission not found.</div>
          </div>
        </div>
      </Layout>
    );
  }
  if (!m) {
    return (
      <Layout>
        <div className="px-4 md:px-6 py-20 mono text-ink-muted">// loading transmission…</div>
      </Layout>
    );
  }

  const ingested = m.created_at ? new Date(m.created_at).toLocaleString() : "—";
  const isFullBleed = m.media_type === "image" || m.media_type === "pdf";

  return (
    <Layout>
      <section data-testid="media-detail-page" className="px-4 md:px-6 pt-6 pb-12">
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <Link to={`/dossiers/${m.dossier_slug}`} data-testid="media-back-link" className="pill pill-sm">← /{m.dossier_slug}</Link>
          <span className="pill pill-yellow pill-sm" data-testid="media-ak-id">{m.ak_id}</span>
          <span className="pill pill-sm">{m.status}</span>
          <span className="pill pill-sm">{m.media_type.toUpperCase()}</span>
          <span className="pill pill-sm">{m.source_type === "url" ? "External" : "Internal"}</span>
          {m.ocr_text && <span className="pill pill-sm" style={{ background: "#fff10f", color: "#000", borderColor: "#fff10f" }} data-testid="media-ocr-badge">⌕ Full text searchable</span>}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 md:gap-4">
          {/* Big white-framed media */}
          <div className={`lg:col-span-8 tile ${isFullBleed ? "" : "!aspect-auto"}`} data-testid="media-viewer" style={isFullBleed ? {} : { aspectRatio: "auto" }}>
            <MediaViewer media={m} />
          </div>

          {/* Metadata rail */}
          <aside className="lg:col-span-4 flex flex-col gap-8" data-testid="media-metadata">
            <div>
              <div className="label-overline mb-3">// Title</div>
              <h1 className="heavy text-4xl md:text-5xl leading-[0.95] break-words">{m.title}</h1>
            </div>

            {m.description && (
              <div>
                <div className="label-overline mb-3">// Description</div>
                <p className="mono text-[13px] leading-relaxed text-white/90">{m.description}</p>
              </div>
            )}

            <div>
              <div className="label-overline mb-3">// Meta</div>
              <div className="flex flex-wrap gap-2">
                <Chip label="Dossier" value={`/${m.dossier_slug}`} />
                <Chip label="Year" value={m.year || "—"} />
                <Chip label="Author" value={m.author || "—"} />
                <Chip label="Type" value={m.media_type} />
              </div>
            </div>

            {m.tags?.length > 0 && (
              <div>
                <div className="label-overline mb-3">// Tags</div>
                <div className="flex flex-wrap gap-2">
                  {m.tags.map((t) => <span key={t} className="pill pill-sm">{t}</span>)}
                </div>
              </div>
            )}

            {m.ocr_text && (
              <div data-testid="media-ocr-preview">
                <div className="label-overline mb-3 flex items-center gap-2">
                  // Extracted text
                  <span className="text-yellow">◆</span>
                </div>
                <div className="hairline p-4 max-h-[240px] overflow-y-auto">
                  <p className="mono text-[11px] leading-relaxed text-white/80 whitespace-pre-wrap">
                    {m.ocr_text.slice(0, 1200)}{m.ocr_text.length > 1200 ? "…" : ""}
                  </p>
                </div>
              </div>
            )}

            <div className="mono text-[10px] uppercase tracking-[0.14em] text-ink-muted">
              Ingested · {ingested}
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}

const Chip = ({ label, value }) => (
  <div className="pill pill-sm !py-2 !px-4 !gap-2">
    <span className="text-white/50">{label}</span>
    <span className="text-white">{String(value).toLowerCase()}</span>
  </div>
);

const MediaViewer = ({ media }) => {
  const url = resolveMediaUrl(media.source_url);
  const thumb = resolveMediaUrl(media.thumbnail_url || media.source_url);

  if (media.media_type === "video") {
    if (/youtube\.com|youtu\.be/.test(url)) {
      const ytId = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]+)/)?.[1];
      return (
        <div className="w-full h-full">
          {ytId ? (
            <iframe title={media.title} src={`https://www.youtube.com/embed/${ytId}`} className="w-full h-full min-h-[500px]" allowFullScreen />
          ) : (
            <video src={url} controls className="w-full h-full" />
          )}
        </div>
      );
    }
    return <video src={url} poster={thumb} controls className="w-full h-full" />;
  }
  if (media.media_type === "pdf") {
    return <iframe src={url} title={media.title} className="w-full h-full min-h-[80vh] bg-white" />;
  }
  if (media.media_type === "audio") {
    return (
      <div className="p-8 w-full">
        {thumb && <img src={thumb} alt={media.title} className="w-full max-h-[60vh] object-contain mb-6" />}
        <audio src={url} controls className="w-full" />
      </div>
    );
  }
  if (media.media_type === "embed") {
    return <iframe src={url} title={media.title} className="w-full h-full min-h-[80vh]" />;
  }
  return <img src={url} alt={media.title} className="w-full h-full object-contain" />;
};
